import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthProvider';

interface RequirePermissionProps {
  /** Single required permission */
  permission?: string;
  /** Pass if any one of these permissions is enough */
  anyOf?: string[];
}

/**
 * AuthGuard already blocks until auth settles. A stable placeholder while
 * permissions resolve avoids mounting protected pages early (and avoids the
 * remount/refetch thrash of returning null).
 */
const RequirePermission: React.FC<RequirePermissionProps> = ({
  permission,
  anyOf,
}) => {
  const { hasPermission, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  const allowed = anyOf?.length
    ? anyOf.some((p) => hasPermission(p))
    : permission
      ? hasPermission(permission)
      : false;

  if (!allowed) {
    return <Navigate to="/insufficient-permissions" replace />;
  }

  return <Outlet />;
};

export default RequirePermission;
